import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, ViewEncapsulation, afterNextRender, computed, effect, forwardRef, inject, input, output, signal, viewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime } from 'rxjs';

@Component({
  selector: 'w-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SearchComponent), multi: true },
  ],
  host: {
    '[class.w-search-small]': '$size() === "Small"',
    '[class.w-search-medium]': '$size() === "Medium"',
  },
})
export class SearchComponent implements ControlValueAccessor {

  private readonly destroyRef = inject(DestroyRef);
  private readonly ngControl = inject(NgControl, { self: true, optional: true });
  private readonly search$ = new Subject<string>();
  private readonly $input = viewChild<ElementRef<HTMLInputElement>>('input');
  private readonly $internalValue = signal('');
  private readonly $disabledFromForm = signal(false);

  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  public $placeholder = input('Search', { alias: 'placeholder' });
  public $ariaLabel = input<string | null>(null, { alias: 'ariaLabel' });
  public $value = input<string | null | undefined>(undefined, { alias: 'value' });
  public $size = input<'Small' | 'Medium'>('Small', { alias: 'size' });
  public $autoFocus = input(false, { alias: 'autoFocus' });
  public $disabled = input(false, { alias: 'disabled' });

  public search = output<string>();

  protected $effectiveDisabled = computed(() => this.$disabled() || this.$disabledFromForm());
  protected $invalid = computed(() => !!this.ngControl?.invalid);
  protected $displayValue = computed(() => this.$internalValue());

  public constructor() {

    afterNextRender(() => {

      if (!this.$autoFocus()) return;

      requestAnimationFrame(() => this.$input()?.nativeElement.focus());
    });

    this.search$
      .pipe(
        debounceTime(300),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(value => this.search.emit(value));

    effect(() => this.syncExternalValue());
  }

  public writeValue(value: string | null): void {

    this.$internalValue.set(value ?? '');
  }

  public registerOnChange(fn: (value: string) => void): void {

    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {

    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {

    this.$disabledFromForm.set(isDisabled);
  }

  protected onInput(event: Event) {

    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.$internalValue.set(value);
    this.onChange(value);
    this.search$.next(value);
  }

  protected onBlur() {

    this.onTouched();
  }

  private syncExternalValue() {

    const value = this.$value();
    if (value === undefined) return;

    this.$internalValue.set(value ?? '');
  }
}
